import React, { useState } from 'react';
import { X, FileText, Shield, Mail, Phone, Check } from 'lucide-react';

export default function TermsModal({ isOpen, onClose, onAgree, isDarkMode }) {
  const [activeTab, setActiveTab] = useState('terms'); // 'terms' or 'privacy'

  if (!isOpen) return null;

  return (
    // FIX 1: Changed 'items-start pt-...' to 'items-center' to center vertically
    // FIX 2: Added 'p-4' for safe spacing on all sides
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      
      <div 
        className={`w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200 ${
          isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'
        }`}
      >
        {/* Header */}
        <div className={`p-5 border-b flex justify-between items-center flex-shrink-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              Legal Information
            </h2>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Vidyazon • Last updated: December 2025
            </p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors`}>
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex border-b flex-shrink-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setActiveTab('terms')}
            className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors relative ${
              activeTab === 'terms' ? 'text-blue-600' : (isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
            }`}
          >
            <FileText className="w-4 h-4" />
            Terms & Conditions
            {activeTab === 'terms' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600"></div>}
          </button>
          <button
            onClick={() => setActiveTab('privacy')}
            className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors relative ${
              activeTab === 'privacy' ? 'text-blue-600' : (isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
            }`}
          >
            <Shield className="w-4 h-4" />
            Privacy Policy
            {activeTab === 'privacy' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600"></div>}
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          
          {/* TERMS CONTENT */}
          {activeTab === 'terms' && (
            <div className="space-y-4">
              <section>
                <h3 className="text-lg font-bold mb-2 text-blue-600">1. Introduction</h3>
                <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Welcome to Vidyazon. By accessing our website and using our services (Admission, Counseling, Coaching, Training), you agree to be bound by these Terms and Conditions.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-bold mb-2 text-blue-600">2. User Accounts</h3>
                <ul className={`list-disc pl-5 text-sm space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <li>You must provide accurate and complete information during registration.</li>
                  <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                  <li>Vidyazon reserves the right to terminate accounts that violate our community guidelines.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-bold mb-2 text-blue-600">3. Services & Payments</h3>
                <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  We offer various educational services. Pricing is subject to change. Refunds are processed according to our specific Refund Policy available on the student dashboard.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-bold mb-2 text-blue-600">4. Contact & Support</h3>
                <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-blue-50 border-blue-100'}`}>
                  <p className="text-sm mb-4 font-medium">For any legal concerns or support regarding these terms:</p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600">
                        <Mail className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Email Us</p>
                        <a href="mailto:info@vidyazon.com" className="font-semibold hover:underline block mb-1">info@vidyazon.com</a>
                        <span className="text-xs text-gray-400">We reply within 24 hours</span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg text-green-600">
                        <Phone className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Call Us</p>
                        <a href="tel:+919997307305" className="font-semibold hover:underline block mb-1">+91 99973 07305</a>
                        <span className="text-xs text-gray-400">24/7 Support Available</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* PRIVACY CONTENT */}
          {activeTab === 'privacy' && (
            <div className="space-y-4">
              <section>
                <h3 className="text-lg font-bold mb-2 text-blue-600">1. Data Collection</h3>
                <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  We collect information you provide directly to us, such as your name, email address, phone number, and educational background when you create an account.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-bold mb-2 text-blue-600">2. How We Use Your Data</h3>
                <ul className={`list-disc pl-5 text-sm space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <li>To provide and maintain our Service.</li>
                  <li>To notify you about changes to our Service.</li>
                  <li>To provide customer support via email or phone.</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-bold mb-2 text-blue-600">3. Data Security</h3>
                <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  The security of your data is important to us. We use industry-standard encryption to protect your personal information during transmission and storage.
                </p>
              </section>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-5 border-t flex-shrink-0 flex flex-col sm:flex-row justify-end gap-3 ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
          <button
            onClick={onClose}
            className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
              isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-200'
            }`}
          >
            Close
          </button>
          <button
            onClick={onAgree}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2"
          >
            <Check className="w-4 h-4" />
            I Agree
          </button>
        </div>
      </div>
    </div>
  );
}